import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminChangePassword.css';
import { Alert, LinearProgress } from '@mui/material';
import { API_BASE_URL } from '../config/apiConfig';

function AdminChangePassword({ token }) {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [strength, setStrength] = useState(0);
  const [countdown, setCountdown] = useState(3);

  const checkStrength = password => {
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[@$!%*?&#]/.test(password)) score += 1;
    return score;
  };

  useEffect(() => {
    setStrength(checkStrength(newPassword));
  }, [newPassword]);

  const handleSubmit = async e => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage("❌ New passwords don't match");
      return;
    }

    if (strength < 4) {
      setMessage('❌ Password is too weak. Try including uppercase letters, numbers, and symbols.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await res.json();
      setMessage(data.message || 'Password change failed');

      if (res.ok && data.message?.toLowerCase().includes('password changed')) {
        setSuccess(true);
        setCountdown(3); // reset for countdown
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
    } catch {
      setMessage('Network error');
    }
  };

  useEffect(() => {
    if (success) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      const interval = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 3000);

      const timer = setTimeout(() => navigate('/admin/login'), 3000);

      return () => {
        clearInterval(interval);
        clearTimeout(timer);
      };
    }
  }, [success, navigate]);

  return (
    <form className="password-change-form" onSubmit={handleSubmit}>
      <h2>🔒 Change Password</h2>

      <input
        type="password"
        placeholder="Current Password"
        value={currentPassword}
        onChange={e => setCurrentPassword(e.target.value)}
        required
      />

      <input
        type="password"
        placeholder="New Password"
        value={newPassword}
        onChange={e => setNewPassword(e.target.value)}
        required
      />

      <LinearProgress
        variant="determinate"
        value={strength * 20}
        color={strength >= 4 ? 'success' : 'warning'}
        style={{ margin: '8px 0', height: '8px', borderRadius: '4px' }}
      />
      <div style={{ fontSize: '0.85rem', color: strength >= 4 ? 'green' : 'orange' }}>
        {strength >= 4 ? 'Strong password 💪' : 'Try making it stronger...'}
      </div>

      <input
        type="password"
        placeholder="Confirm New Password"
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        required
      />

      <button type="submit">Change Password</button>

      {/* Message shown only if not success */}
      {message && !success && (
        <p className="change-password-message">{message}</p>
      )}

      {/* Success block with Alert, timer, and button */}
      {success && (
        <div className="change-password-success">
          <Alert severity="success" style={{ marginTop: '1rem' }}>
            ✅ Password changed successfully! Redirecting to login in {countdown} seconds…
          </Alert>
          <button
            style={{ marginTop: '1rem' }}
            onClick={() => navigate('/admin/login')}
          >
            Go to Login Now
          </button>
        </div>
      )}
    </form>
  );
}

export default AdminChangePassword;
